import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { SmartIrrigationService } from 'src/api/services';
import { SmartIrrigationControllerDashboard, SmartIrrigationDashboard, SmartIrrigationRootNodeDto } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { forkJoin } from 'rxjs';

interface RegionParkSummary {
  id: number;
  name: string;
  controllerCount: number;
  activeAlarmCount: number;
}

interface RegionCard {
  id: number;
  name: string;
  parkCount: number;
  controllerCount: number;
  parks: RegionParkSummary[];
}

@Component({
  selector: 'app-smart-irrigation-regions',
  templateUrl: './regions.component.html',
  styleUrls: ['./regions.component.scss']
})
export class SmartIrrigationRegionsComponent implements OnInit {
  loading = true;
  lastRefresh: Date | null = null;
  regions: RegionCard[] = [];

  constructor(
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    forkJoin({
      rootNode: this.irrigation.SmartIrrigationGetRootNode(this.app.customerId),
      dashboard: this.irrigation.SmartIrrigationGetDashboard(this.app.customerId)
    }).subscribe({
      next: ({ rootNode, dashboard }) => {
        this.regions = this.buildRegions(rootNode, dashboard);
        this.lastRefresh = new Date();
        this.loading = false;
      },
      error: () => {
        this.regions = [];
        this.loading = false;
      }
    });
  }

  viewPark(park: RegionParkSummary): void {
    this.router.navigate(['/smart-irrigation/parks', park.id]);
  }

  viewRegion(region: RegionCard): void {
    this.router.navigate(['/smart-irrigation/region', region.id]);
  }

  private buildRegions(rootNode: SmartIrrigationRootNodeDto, dashboard: SmartIrrigationDashboard): RegionCard[] {
    const controllersBySiteUri = new Map<string, SmartIrrigationControllerDashboard>();
    (dashboard.irrigationControllers || []).forEach(controller => {
      if (controller.siteUri) {
        controllersBySiteUri.set(this.normalize(controller.siteUri), controller);
      }
    });

    return (rootNode.childNodes || [])
      .map(region => this.buildRegionCard(region, controllersBySiteUri))
      .filter(region => region.parkCount > 0);
  }

  private buildRegionCard(
    region: SmartIrrigationRootNodeDto,
    controllersBySiteUri: Map<string, SmartIrrigationControllerDashboard>
  ): RegionCard {
    // A Park is a node whose Field children (grandchildren of the region) host controller devices.
    const parks: RegionParkSummary[] = (region.childNodes || [])
      .filter(child => (child.childNodes || []).some(field => this.nodeContainsController(field)))
      .map(child => ({
        id: child.id,
        name: child.name || `Park ${child.id}`,
        controllerCount: (child.childNodes || []).reduce((sum, grandchild) => sum + this.countControllers(grandchild), 0),
        activeAlarmCount: (child.childNodes || []).reduce(
          (sum, grandchild) => sum + this.countAlarms(grandchild, controllersBySiteUri),
          0
        )
      }));

    return {
      id: region.id,
      name: region.name || `Region ${region.id}`,
      parkCount: parks.length,
      controllerCount: parks.reduce((sum, park) => sum + park.controllerCount, 0),
      parks
    };
  }

  // A node is a park when at least one of its children hosts a controller device.
  private nodeContainsController(node: SmartIrrigationRootNodeDto): boolean {
    return (node.associatedDevices || []).some(device =>
      (device.deviceTypeName || '').toLowerCase().includes('controller')
    );
  }

  private countControllers(node: SmartIrrigationRootNodeDto): number {
    return (node.associatedDevices || []).filter(device =>
      (device.deviceTypeName || '').toLowerCase().includes('controller')
    ).length;
  }

  private countAlarms(
    node: SmartIrrigationRootNodeDto,
    controllersBySiteUri: Map<string, SmartIrrigationControllerDashboard>
  ): number {
    return (node.associatedDevices || [])
      .filter(device => (device.deviceTypeName || '').toLowerCase().includes('controller'))
      .reduce((sum, device) => sum + (controllersBySiteUri.get(this.normalize(device.uri))?.alarms || []).length, 0);
  }

  private normalize(value: string): string {
    return value.trim().toLowerCase();
  }
}
